import { DomainError, SyncError, TransportError, } from "@econ-v1/domain";
import { isL402PaymentChallengeError, L402PaymentFailedError, L402PaymentRetryDeniedError } from "./client-rpc/response-error.js";
/**
 * Adapts the fixed-priority transport selector to the kernel's long-lived
 * HomeNodeClient seam. The kernel never needs to know which candidate won.
 */
export class ManagedHomeNodeClient {
    #listeners = new Set();
    #manager;
    #onTransportFailure;
    #paymentHandler;
    #active;
    #activation;
    #closedClients = new WeakSet();
    #closed = false;
    #unsubscribeManager;
    #unsubscribeActive = () => undefined;
    state;
    constructor(manager, options = {}) {
        this.#manager = manager;
        this.state = manager.state;
        this.#onTransportFailure = options.onTransportFailure ?? (() => undefined);
        this.#paymentHandler = options.paymentHandler;
        this.#unsubscribeManager = manager.subscribe((state) => this.#publishState(state));
        if (options.initial !== undefined) {
            this.#activate(options.initial);
            if (options.initial.state.status === "connected" && isHttpPath(options.initial.state.path)) {
                this.#manager.markConnected(options.initial.state.path);
            }
        }
    }
    async invoke(request) {
        const client = await this.#ensureClient();
        try {
            return await client.invoke(request);
        }
        catch (error) {
            if (isPathFailure(error)) {
                await this.#retire(client);
                throw error;
            }
            if (isL402PaymentChallengeError(error))
                return await this.#payAndRetry(client, request, error);
            throw error;
        }
    }
    /**
     * Pay-and-retry for an L402 challenge (ticket #1866). Retries the
     * ORIGINAL request at most once, with the paid `LSAT <macaroon>:<preimage>`
     * token attached as the `x-l402` header the client-rpc dispatch bridge
     * reads (`core/adapters-network/src/client_rpc/dispatch.rs`). No handler
     * configured → the challenge is surfaced unchanged (never a fabricated
     * payment); a payment failure or a second challenge on the retried
     * request both surface as a distinct, terminal error — never a loop, and
     * never a `TransportError` that would trigger rung failover.
     */
    async #payAndRetry(client, request, challenge) {
        if (this.#paymentHandler === undefined)
            throw challenge;
        let token;
        try {
            const { preimage } = await this.#paymentHandler.pay({
                amountSats: challenge.amountSats,
                description: challenge.description,
                invoice: challenge.invoice,
                macaroon: challenge.macaroon,
            });
            token = `LSAT ${challenge.macaroon}:${preimage}`;
        }
        catch (paymentError) {
            throw new L402PaymentFailedError(paymentError);
        }
        try {
            return await client.invoke({
                ...request,
                headers: { ...request.headers, "x-l402": token },
            });
        }
        catch (retryError) {
            if (isL402PaymentChallengeError(retryError))
                throw new L402PaymentRetryDeniedError();
            if (isPathFailure(retryError))
                await this.#retire(client);
            throw retryError;
        }
    }
    async *deltas(request) {
        const client = await this.#ensureClient();
        try {
            for await (const delta of client.deltas(request))
                yield delta;
        }
        catch (error) {
            if (isPathFailure(error))
                await this.#retire(client);
            throw error;
        }
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }
    close() {
        this.#closed = true;
        this.#manager.abort();
        this.#unsubscribeActive();
        this.#unsubscribeActive = () => undefined;
        const active = this.#active;
        this.#active = undefined;
        if (active !== undefined)
            void this.#closeClient(active);
        this.#manager.markOffline();
        this.#unsubscribeManager();
        this.#listeners.clear();
    }
    async disconnect() {
        if (this.#closed)
            return;
        this.#manager.abort();
        this.#unsubscribeActive();
        this.#unsubscribeActive = () => undefined;
        const active = this.#active;
        this.#active = undefined;
        if (active !== undefined)
            await this.#closeClient(active);
        this.#manager.markOffline();
    }
    async replace(client) {
        if (this.#closed) {
            await this.#closeClient(client);
            return;
        }
        if (this.#active === client)
            return;
        const active = this.#active;
        this.#unsubscribeActive();
        this.#unsubscribeActive = () => undefined;
        this.#active = undefined;
        if (active !== undefined)
            await this.#closeClient(active);
        if (this.#closed) {
            await this.#closeClient(client);
            return;
        }
        this.#activate(client);
    }
    async #ensureClient() {
        if (this.#closed)
            throw closedError();
        if (this.#active !== undefined)
            return this.#active;
        if (this.#activation !== undefined)
            return await this.#activation;
        const activation = this.#activateWhenConnected();
        this.#activation = activation;
        try {
            return await activation;
        }
        finally {
            if (this.#activation === activation)
                this.#activation = undefined;
        }
    }
    async #activateWhenConnected() {
        let client;
        try {
            client = await this.#manager.connect();
        }
        catch (error) {
            if (this.#closed)
                throw closedError();
            throw normalizeTransportError(error, "No home-node transport available");
        }
        if (this.#closed) {
            await this.#closeClient(client);
            throw closedError();
        }
        if (this.#active !== undefined) {
            if (this.#active !== client)
                await this.#closeClient(client);
            return this.#active;
        }
        this.#activate(client);
        return client;
    }
    async #retire(client) {
        if (this.#active !== client)
            return;
        this.#unsubscribeActive();
        this.#unsubscribeActive = () => undefined;
        this.#active = undefined;
        await this.#closeClient(client);
        this.#manager.markOffline();
        if (!this.#closed)
            void Promise.resolve(this.#onTransportFailure()).catch(() => undefined);
    }
    #activate(client) {
        this.#active = client;
        this.#unsubscribeActive = client.subscribe?.((event) => {
            if (event.type === "transport.state-changed") {
                if (event.state.status === "offline")
                    void this.#retire(client);
                this.#publishState(event.state);
                return;
            }
            for (const listener of [...this.#listeners])
                listener(event);
        }) ?? (() => undefined);
        // `subscribe()` only ever delivers *future* transitions — a client that
        // already settled into its current state before being handed to
        // `#activate` (every non-HTTP carrier: `WebTransportHomeNodeClient` is
        // already `connected` at construction, `WebRtcHomeNodeClient` finishes
        // its own connect handshake — including the `webrtc-direct` transition
        // — before a promotion controller ever subscribes to or replaces onto
        // it) would otherwise never have that state observed here. Adopting it
        // synchronously is idempotent with whatever `subscribe()`/the caller's
        // own `TransportManager.markConnected()` publishes next (`#publishState`
        // bails out when nothing actually changed).
        this.#publishState(client.state);
    }
    async #closeClient(client) {
        if (this.#closedClients.has(client))
            return;
        this.#closedClients.add(client);
        await closeClient(client);
    }
    #publishState(state) {
        if (this.state.status === state.status && this.state.path === state.path)
            return false;
        this.state = state;
        const event = { state, type: "transport.state-changed" };
        for (const listener of [...this.#listeners])
            listener(event);
        return true;
    }
}
function normalizeTransportError(error, fallback) {
    if (error instanceof DomainError || error instanceof SyncError || error instanceof TransportError)
        return error;
    return new TransportError({ code: "offline", message: fallback });
}
function isPathFailure(error) {
    return error instanceof TransportError &&
        (error.code === "offline" || error.code === "connection_lost" || error.code === "unavailable");
}
function closedError() {
    return new TransportError({ code: "offline", message: "Home node client is closed" });
}
async function closeClient(client) {
    if (client.close === undefined)
        return;
    await Promise.resolve(client.close()).catch(() => undefined);
}
/**
 * Every path the direct-ladder `TransportManager` selects and can accept via
 * `markConnected` (ticket #1823: browser-trusted LAN HTTPS, hash-pinned LAN
 * WebTransport, WAN HTTPS/WSS) — excludes `iroh-relay`, which that manager
 * never selects.
 */
function isHttpPath(path) {
    return path === "lan-direct" || path === "lan-webtransport" || path === "wan-direct";
}
//# sourceMappingURL=managed-home-node-client.js.map